// ThemeContext.js
import React, { createContext, useState, useMemo, useEffect } from "react";
import { alpha, createTheme, darken, lighten, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { themeColors, fonts } from "../utilities/themeColors";
import { useOptions } from "./optionContext";
export const ThemeContext = createContext();

const DEFAULT_ACCENT = "blue";
const DEFAULT_FONT = "Poppins";
const isHexColor = (value) => typeof value === "string" && /^#([0-9A-F]{3}){1,2}$/i.test(value.trim());

const resolveAccentPalette = (value) => {
	if (value && themeColors[value]) {
		return themeColors[value];
	}
	if (isHexColor(value)) {
		const color = value.trim();
		return {
			main: color,
			light: lighten(color, 0.15),
			dark: darken(color, 0.2),
			contrastText: "#fff",
		};
	}
	const fallback = themeColors[DEFAULT_ACCENT];
	return {
		main: fallback.main,
		light: fallback.light,
		dark: fallback.dark,
		contrastText: fallback.contrastText,
	};
};

export const ThemeContextProvider = ({ children }) => {
    const [mode, setMode] = useState(localStorage.getItem("themeMode") || "light");
    const [accentColor, setAccentColor] = useState(localStorage.getItem("accentColor") || DEFAULT_ACCENT);
    const defaultHeaderFontKey = localStorage.getItem("headerFontKey") || DEFAULT_FONT;
    const defaultBodyFontKey = localStorage.getItem("bodyFontKey") || DEFAULT_FONT;

    const [headerFontKey, setHeaderFontKey] = useState(defaultHeaderFontKey);
    const [bodyFontKey, setBodyFontKey] = useState(defaultBodyFontKey);
    const { optionKeys } = useOptions();

    const headerFontFamily = fonts[headerFontKey];
    const bodyFontFamily = fonts[bodyFontKey];
    const accentPalette = useMemo(() => resolveAccentPalette(accentColor), [accentColor]);

    useEffect(() => {
        if (!optionKeys) return;
        const storedAccent = optionKeys.accentColor || optionKeys.theme;
        if (storedAccent) {
            setAccentColor((prev) => (prev === storedAccent ? prev : storedAccent));
        }
        if (optionKeys.headerFontKey) {
            setHeaderFontKey((prev) =>
                prev === optionKeys.headerFontKey ? prev : optionKeys.headerFontKey
            );
        }
        if (optionKeys.bodyFontKey) {
            setBodyFontKey((prev) =>
                prev === optionKeys.bodyFontKey ? prev : optionKeys.bodyFontKey
            );
        }
    }, [optionKeys]);

    useEffect(() => {
        localStorage.setItem("headerFontKey", headerFontKey);
    }, [headerFontKey]);

    useEffect(() => {
        localStorage.setItem("bodyFontKey", bodyFontKey);
    }, [bodyFontKey]);

    const flexRow = {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "flex-start",
    };

    const flexCol = {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
    };

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: {
                        light: lighten(accentPalette.main, 0.1),
                        main: accentPalette.main,
                        dark: darken(accentPalette.main, 0.2),
                        contrastText: accentPalette.contrastText || "#fff",
                    },
                    background: {
                        default: mode === "light" ? "#fafafa" : "#303030",
                        paper: mode === "light" ? "#ffffff" : "#424242",
                    },
                    text: {
                        primary: mode === "light" ? "#000000" : "#ffffff",
                    },
                    customBorder: mode === "light" ? "#eee" : "transparent",
                },
                typography: {
                    fontFamily: bodyFontFamily,
                    fontSize: 13,
                    h1: { fontFamily: headerFontFamily, fontSize: "30px", fontWeight: 600 },
                    h2: { fontFamily: headerFontFamily, fontSize: "26px", fontWeight: 600 },
                    h3: { fontFamily: headerFontFamily, fontSize: "22px", fontWeight: 500 },
                    h4: { fontFamily: headerFontFamily, fontSize: "20px", fontWeight: 500 },
                    h5: { fontFamily: headerFontFamily, fontSize: "18px", fontWeight: 500 },
                    h6: { fontFamily: headerFontFamily, fontSize: "16px", fontWeight: 500 },
                    button: { fontFamily: headerFontFamily, textTransform: "none", fontWeight: 500 },
                    body1: { fontFamily: bodyFontFamily, fontSize: "13px" },
                    body2: { fontFamily: bodyFontFamily, fontSize: "13px" },
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: {
                            body: { fontFamily: bodyFontFamily },
                            "*": { fontFamily: bodyFontFamily },
                        },
                    },
                    MuiButton: {
                        defaultProps: {
                            size: "medium",
                        },
                        styleOverrides: {
                            root: {
                                textTransform: "none",
                                fontSize: "12px",
                                paddingInline: 20,
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                                fontFamily: headerFontFamily,
                            },
                            label: {
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                display: "block", // important so ellipsis can work
                                maxWidth: "100%", // keeps it from overflowing parent
                            },

                            // Override for contained variant (any color)
                            contained: {},

                            textPrimary: {
                                color: accentPalette.main,
                                "&:hover": {
                                    backgroundColor: alpha(accentPalette.light, 0.1),
                                },
                            },
                        },
                        variants: [
                            {
                                props: { variant: "roundedSquareContained" },
                                style: ({ theme, ownerState }) => {
                                    let colorValue;
                                    let colorObj;

                                    // Check if it's a theme palette key
                                    if (ownerState.color && theme.palette[ownerState.color]) {
                                        colorObj = theme.palette[ownerState.color];
                                    }
                                    // Check if it's a valid hex code
                                    else if (ownerState.color && /^#([0-9A-F]{3}){1,2}$/i.test(ownerState.color)) {
                                        colorValue = ownerState.color;
                                        colorObj = {
                                            main: colorValue,
                                            light: colorValue,
                                        };
                                    }
                                    // Fallback to primary palette
                                    else {
                                        colorObj = theme.palette.primary;
                                    }

                                    return {
                                        width: 35,
                                        height: 35,
                                        minWidth: 35,
                                        minHeight: 35,
                                        borderRadius: "50% !important",
                                        backgroundColor: alpha(colorObj.main, 0.1),
                                        color: colorObj.main,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        "&:hover": {
                                            backgroundColor: alpha(colorObj.light, 0.2),
                                        },
                                        display: "flex",
                                        alignItems: "center",
                                        border: "0px",
                                        justifyContent: "center",
                                        padding: 0,
                                        lineHeight: 1,
                                        "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                                            margin: 0, // Remove MUI's default icon margin
                                        },
                                        "&.Mui-disabled": {
                                            backgroundColor: "#e0e0e0", // light gray background when disabled
                                            color: "#9e9e9e", // muted gray text/icon color
                                        },
                                    };
                                },
                            },

                            {
                                props: { variant: "roundedOutlinedSquare" },
                                style: {
                                    width: 35,
                                    height: 35,
                                    minWidth: 35,
                                    minHeight: 35,
                                    borderRadius: "50%",
                                    border: "1px solid",
                                    borderColor: accentPalette.main,
                                    color: accentPalette.main,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    "&:hover": {
                                        backgroundColor: alpha(accentPalette.main, 0.08),
                                        borderColor: accentPalette.dark,
                                        color: accentPalette.dark,
                                    },
                                },
                            },
                            {
                                props: { variant: "roundedGreyBgSquareContained" },
                                style: ({ theme, ownerState }) => {
                                    let colorValue;
                                    let colorObj;

                                    // Check if it's a theme palette key
                                    if (ownerState.color && theme.palette[ownerState.color]) {
                                        colorObj = theme.palette[ownerState.color];
                                    }
                                    // Check if it's a valid hex code
                                    else if (ownerState.color && /^#([0-9A-F]{3}){1,2}$/i.test(ownerState.color)) {
                                        colorValue = ownerState.color;
                                        colorObj = {
                                            main: colorValue,
                                            light: colorValue,
                                        };
                                    }
                                    // Fallback to primary palette
                                    else {
                                        colorObj = theme.palette.primary;
                                    }

                                    return {
                                    width: 35,
                                    height: 35,
                                    minWidth: 35,
                                    minHeight: 35,
                                    borderRadius: "50%",
                                        backgroundColor: alpha(colorObj.main, 0.1),
                                        color: colorObj.main,
                                    fontSize: "14px",
                                    padding: "5px",
                                    // boxShadow: "inset 0 0 0 1px #ccc",
                                    display: "flex",
                                    alignItems: "center",
                                    border: "0px",
                                    justifyContent: "center",
                                    padding: 0,
                                    lineHeight: 1,
                                    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                                        margin: 0, // Remove MUI's default icon margin
                                    },
                                    "&:hover": {
                                        backgroundColor: alpha(colorObj.main, 0.2),
                                    },
                                    "&.Mui-disabled": {
                                        backgroundColor: "#e0e0e0", // light gray background when disabled
                                        color: "#9e9e9e", // muted gray text/icon color
                                    },
                                    }
                                },
                            },
                            {
                                props: { variant: "roundedGreyBgSquareContainedSmall" },
                                style: ({ theme, ownerState }) => {
                                    let colorValue;
                                    let colorObj;

                                    // Check if it's a theme palette key
                                    if (ownerState.color && theme.palette[ownerState.color]) {
                                        colorObj = theme.palette[ownerState.color];
                                    }
                                    // Check if it's a valid hex code
                                    else if (ownerState.color && /^#([0-9A-F]{3}){1,2}$/i.test(ownerState.color)) {
                                        colorValue = ownerState.color;
                                        colorObj = {
                                            main: colorValue,
                                            light: colorValue,
                                        };
                                    }
                                    // Fallback to primary palette
                                    else {
                                        colorObj = theme.palette.primary;
                                    }

                                    return {
                                        width: 28,
                                        height: 28,
                                        minWidth: 28,
                                        minHeight: 28,
                                        borderRadius: "50%",
                                        backgroundColor: alpha(colorObj.main, 0.1),
                                        color: colorObj.main,
                                        fontSize: "12px",
                                        padding: "3px",
                                        // boxShadow: "inset 0 0 0 1px #ccc",
                                        display: "flex",
                                        alignItems: "center",
                                        border: "0px",
                                        justifyContent: "center",
                                        padding: 0,
                                        lineHeight: 1,
                                        "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                                            margin: 0, // Remove MUI's default icon margin
                                        },
                                        "&:hover": {
                                            backgroundColor: alpha(colorObj.main, 0.2),
                                        },
                                        "&.Mui-disabled": {
                                            backgroundColor: "#e0e0e0", // light gray background when disabled
                                            color: "#9e9e9e", // muted gray text/icon color
                                        },
                                    }
                                },
                            },
                            {
                                props: { variant: "modelMenuButtons" },
                                style: ({ theme, ownerState }) => {
                                    let colorValue;
                                    let colorObj;

                                    // Check if it's a theme palette key
                                    if (ownerState.color && theme.palette[ownerState.color]) {
                                        colorObj = theme.palette[ownerState.color];
                                    }
                                    // Check if it's a valid hex code
                                    else if (ownerState.color && /^#([0-9A-F]{3}){1,2}$/i.test(ownerState.color)) {
                                        colorValue = ownerState.color;
                                        colorObj = {
                                            main: colorValue,
                                            light: colorValue,
                                        };
                                    }
                                    // Fallback to primary palette
                                    else {
                                        colorObj = theme.palette.primary;
                                    }

                                    return {
                                        width: 40,
                                        height: 40,
                                        minWidth: 40,
                                        minHeight: 40,
                                        borderRadius: '15px',
                                        backgroundColor: '#fff',
                                        color: colorObj.main,
                                        fontSize: "14px",
                                        padding: "5px",
                                        boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
                                        display: "flex",
                                        alignItems: "center",
                                        border: "0px",
                                        justifyContent: "center",
                                        padding: 0,
                                        lineHeight: 1,
                                        "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                                            margin: 0, // Remove MUI's default icon margin
                                        },
                                        "&:hover": {
                                            backgroundColor: colorObj.main,
                                            boxShadow: "0 0 6px 1px rgba(0,0,0,0.3)",
                                            color: '#fff'
                                        },
                                        "&.Mui-disabled": {
                                            backgroundColor: "#e0e0e0", // light gray background when disabled
                                            color: "#9e9e9e", // muted gray text/icon color
                                        },
                                    }
                                },
                            },
                            {
                                props: { variant: "roundedGreyBgContained" },
                                style: {
                                    borderRadius: "20px",
                                    backgroundColor: alpha(accentPalette.main, 0.08),
                                    color: accentPalette.main,
                                    display: "flex",
                                    alignItems: "center",
                                    border: "0px",
                                    justifyContent: "center",
                                    "&:hover": {
                                        backgroundColor: alpha(accentPalette.main, 0.2),
                                    },
                                },
                            },
                            {
                                props: { variant: "ghostPrimary" },
                                style: {
                                    width: 35,
                                    height: 35,
                                    minWidth: 35,
                                    minHeight: 35,
                                    borderRadius: "50%",
                                    backgroundColor: "transparent",
                                    color: accentPalette.main,
                                    border: `1px dashed ${accentPalette.main}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    "&:hover": {
                                        backgroundColor: alpha(accentPalette.main, 0.08),
                                    },
                                },
                            },
                        ],

                    },
                    MuiTabs: {
                        styleOverrides: {
                            root: {
                                minHeight: 30,
                            },
                            indicator: {
                                height: "2px",
                                borderRadius: "1px",
                                backgroundColor: accentPalette.main,
                            },
                        },
                    },
                    MuiTab: {
                        styleOverrides: {
                            root: {
                                minWidth: "60px",
                                minHeight: 30,
                                textTransform: "none", // keep normal case text
                                fontWeight: "normal",
                                fontSize: "12px",
                                color: "text.secondary",
                                "&.Mui-selected": {
                                    fontWeight: 600,
                                    color: accentPalette.main,
                                },
                                "&:hover": {
                                    color: accentPalette.main,
                                },
                            },
                        },
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            root: {
                                borderRadius: "8px",
                                background: `${mode === "light" ? "#ffffff" : "#424242"} !important`,
                            },
                            input: {
                                background: "transparent !important",
                            },
                        },
                    },
                    MuiInputBase: {
                        styleOverrides: {
                            root: {
                                background: mode === "light" ? "#ffffff" : "#424242",
                            },
                            input: {
                                background: "transparent",
                            },
                        },
                    },
                    MuiPickersOutlinedInput: {
                        styleOverrides: {
                            root: {
                                borderRadius: "8px",
                                background: `${mode === "light" ? "#ffffff" : "#424242"} !important`,
                            },
                            input: {
                                background: "transparent !important",
                            },
                        },
                    },
                    MuiAlert: {
                        styleOverrides: {
                            root: {
                                borderRadius: "8px",
                                fontSize: "12px",
                            },
                            message: {
                                fontSize: "12px",
                            },
                            action: {
                                fontSize: "12px",
                            },
                            icon: {
                                fontSize: "18px", // optional: keeps icon proportionate if text shrinks
                            },
                        },
                    },
                }
            }),
        [mode, accentColor, headerFontFamily, bodyFontFamily]
    );

    useEffect(() => {
        const root = document.documentElement;

        // Primary color variables
        root.style.setProperty("--primary-main", theme.palette.primary.main);
        root.style.setProperty("--primary-light", theme.palette.primary.light);
        root.style.setProperty("--primary-dark", theme.palette.primary.dark);
        root.style.setProperty("--primary-contrast-text", theme.palette.primary.contrastText);
        root.style.setProperty("--primary-hover", theme.palette.primary.light);
        root.style.setProperty("--primary-selected", theme.palette.primary.dark);
        root.style.setProperty("--primary-disabled", theme.palette.primary.light);

        root.style.setProperty("--secondary-main", theme.palette.secondary.main);
		root.style.setProperty("--secondary-light", theme.palette.secondary.light);
		root.style.setProperty("--secondary-dark", theme.palette.secondary.dark);
		root.style.setProperty("--secondary-contrast-text", theme.palette.secondary.contrastText);

        // Font family variable
        root.style.setProperty("--font-family", theme.typography.fontFamily);

        // Font size variables
        root.style.setProperty("--font-size-base", `${theme.typography.fontSize}px`);
        root.style.setProperty("--font-size-h1", theme.typography.h1.fontSize);
        root.style.setProperty("--font-size-h2", theme.typography.h2.fontSize);
        root.style.setProperty("--font-size-h3", theme.typography.h3.fontSize);
        root.style.setProperty("--font-size-body1", theme.typography.body1.fontSize);
        root.style.setProperty("--font-size-body2", theme.typography.body2.fontSize);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem("themeMode", mode);
        localStorage.setItem("accentColor", accentColor);
    }, [mode, accentColor]);

    const toggleMode = () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
    };

    const changeAccentColor = (color) => {
        setAccentColor(color);
    };

    return (
        <ThemeContext.Provider value={{
            toggleMode, changeAccentColor, mode, accentColor, headerFontKey,
            bodyFontKey,
            setHeaderFontKey,
            setBodyFontKey,
        }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};
